import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { ErrorableLoader } from '@src/components';
import { StepListStep } from '@src/components/StepsList';
import {
  useGetNifOrderRowQuery,
  useGetNifOrderWithUploadPoaDataQuery,
} from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import NifOrderLayout from '../components/NifOrderLayout';
import NifOrderUploadPoaForm from './UploadPoaForm';

function UploadNifOrderPoaPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  // Status is needed to gate the heavier query. The `poaTemplateFile` field is
  // only readable while the order is in POA_PENDING; querying it outside that
  // window returns Forbidden.
  const { data: rowData, error: rowError } = useGetNifOrderRowQuery({
    variables: { id },
  });
  const isPoaPending = rowData?.nifOrder.status === 'POA_PENDING';

  const { data, error } = useGetNifOrderWithUploadPoaDataQuery({
    variables: { id },
    skip: !isPoaPending,
  });
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation(['nif']);

  const steps: StepListStep[] = [
    {
      name: t(
        'upload_power_of_attorney_page.sidebar_title',
        'Sign Power of Attorney',
      ),
      status: 'current',
      href: '#',
    },
  ];

  useEffect(() => {
    if (
      rowData &&
      rowData.nifOrder.status !== 'POA_PENDING' &&
      rowData.nifOrder.status !== 'POA_RECEIVED'
    ) {
      toast.error(
        t(
          'upload_power_of_attorney_page.toast_error',
          'This order does not need a POA to be uploaded.',
        ),
      );
    }
  }, [rowData, navigate, t, toast]);

  if (rowData && rowData.nifOrder.status !== 'POA_PENDING') {
    return <Navigate to="/" />;
  }

  if (!data) {
    return <ErrorableLoader error={error ?? rowError} />;
  }

  return (
    <Routes>
      <Route
        element={
          <NifOrderLayout steps={steps} onExitClick={() => navigate('/')} />
        }
      >
        <Route
          index
          element={<NifOrderUploadPoaForm nifOrder={data.nifOrder} />}
        />
      </Route>
    </Routes>
  );
}

export default UploadNifOrderPoaPage;
